// Purchasing API contracts (M6, WP6.1): suppliers and what is bought from them, versioned
// price lists, purchase requests (judged in the approvals inbox), purchase orders, goods
// receipts and the restock suggestion. Wire format as elsewhere: snake_case, quantities as
// decimal strings, money as integer cents (prices per pack, VAT-exclusive), instants
// ISO-8601 UTC, days `YYYY-MM-DD`, discounts in basis points (1250 = 12.5%).
//
//   GET  /purchasing/suppliers                     suppliers, searchable
//   POST /purchasing/suppliers                     a supplier
//   GET  /purchasing/suppliers/:id                 with its items and their current prices
//   PUT  /purchasing/suppliers/:id                 edit (fields become the person's), archive
//   POST /purchasing/suppliers/:id/items           a supplier item
//   PUT  /purchasing/supplier-items/:id            edit or archive a supplier item (the pack
//                                                  only until a price or order uses it)
//   POST /purchasing/supplier-items/:id/replace    a new pack in its place (archives this one)
//   GET  /purchasing/suppliers/:id/price-lists     versions, newest first
//   POST /purchasing/suppliers/:id/price-lists     a new version (discount changes flagged)
//   GET  /purchasing/price-lists/:id               one version, each price against the last
//   POST /purchasing/price-lists/:id/archive       withdraw a version
//   GET  /purchasing/requests                      purchase requests
//   POST /purchasing/requests                      a request (→ an approval request)
//   GET  /purchasing/requests/:id                  one request, with its lines' judgement
//   GET  /purchasing/orders                        orders by status, supplier, expected date
//   POST /purchasing/orders                        an order without a request
//   GET  /purchasing/orders/:id                    lines, VAT, what arrived, its receipts
//   PUT  /purchasing/orders/:id                    edit the expected date, note, lines
//   POST /purchasing/orders/:id/send               mark sent (manual: nothing is sent)
//   POST /purchasing/orders/:id/cancel             cancel (nothing received yet)
//   POST /purchasing/orders/:id/close              close short (stop expecting the rest)
//   GET  /purchasing/receipts                      goods receipts, filterable
//   GET  /purchasing/receipts/:id                  one receipt, its lines, lots and flags
//   GET  /purchasing/receipts/held                 deliveries held for a manager (D-066)
//   GET  /purchasing/restock                       restock suggestions, every number explained
//
// A request is decided through `POST /approvals/:id/decision` (type `purchase_request`,
// permission `purchasing.approve`), product by product with `purchase_lines`; approving
// makes the order (`purchase_order_id` in the answer).
//
// Permissions: reads any `purchasing.*` permission (supplier contacts and prices are not
// for view-only roles; requests, orders, receipts and restock within the caller's branch
// scope); suppliers, items and price lists `purchasing.order` (all branches); a request
// `purchasing.request`; editing, sending, cancelling and closing an order
// `purchasing.order`; changing the lines of an order made from an approved request also
// `purchasing.approve` (D-063); an order without a request `purchasing.order` +
// `purchasing.approve`.
//
// Every POST that creates something (supplier, supplier item, replacement pack, price list,
// request, order) takes an `idempotency-key` header: a repeat with the same key answers
// the first result (D-063).
import { z } from 'zod';
import { flagSchema } from '../commands/envelope.js';
import {
  PO_STATUSES,
  PURCHASE_REQUEST_ORIGINS,
  PURCHASE_REQUEST_STATUSES,
} from '../purchasing/orders.js';
import {
  compareQty,
  nonNegativeQtyString,
  positiveQtyString,
  qtyString,
} from '../quantity/index.js';
import { isPlainDate } from '../time/index.js';
import { uomCode } from '../uom/index.js';
import {
  STOCK_PAGE_DEFAULT,
  STOCK_PAGE_MAX,
  TICKET_VAT_CODES,
  stockLocationRefSchema,
  stockProductRefSchema,
} from './stock.js';
import { branchRefSchema, lotRefSchema, personRefSchema } from './stock-control.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const cents = z.int().min(0);
const count = z.int().min(0);
const bp = z.int().min(0).max(10_000);
const limit = z.coerce.number().int().min(1).max(STOCK_PAGE_MAX).default(STOCK_PAGE_DEFAULT);
const cursor = z.string().min(1).max(512).optional();
const queryBool = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true')
  .optional();
const text = (max: number) => z.string().trim().min(1).max(max);
const leadDays = z.int().min(0).max(365);
const vatCode = z.enum(TICKET_VAT_CODES);

/** `:id` path parameter of the purchasing routes. */
export const purchasingIdParamsSchema = z.object({ id: z.uuid() });

/** Where an imported record came from (connector kind + the source's id). */
export const sourceRefSchema = z.object({ kind: z.string(), ref: z.string() });

/** A supplier reference. */
export const supplierRefSchema = z.object({ id: z.uuid(), name: z.string() });

// ---------------------------------------------------------------------------------------
// Suppliers and supplier items

/** An item's current price (the version in effect today). */
export const currentPriceSchema = z.object({
  price_list_id: z.uuid(),
  version_no: z.int().min(1),
  effective_from: plainDate,
  list_price_cents: cents,
  discount_bp: bp,
  net_price_cents: cents,
});

/** A supplier item: one product bought from one supplier in one order unit. */
export const supplierItemSchema = z.object({
  id: z.uuid(),
  supplier_id: z.uuid(),
  product: stockProductRefSchema,
  supplier_code: z.string().nullable(),
  description: z.string().nullable(),
  /** The order unit (`bag`, `case`…) and how much of the product's base unit one holds. */
  pack_uom: uomCode,
  base_qty_per_pack: qtyString,
  min_order_packs: qtyString.nullable(),
  /** The item's own lead time (null: the supplier's). */
  lead_time_days: leadDays.nullable(),
  preferred: z.boolean(),
  /** Its own VAT code (null: the product's). */
  vat_rate_code: vatCode.nullable(),
  /** The code purchases use. */
  effective_vat_rate_code: vatCode,
  current_price: currentPriceSchema.nullable(),
  /**
   * The pack (unit and size) can no longer change: a price or an order line uses it, and
   * prices are per pack (D-063). Replace it with a new pack instead.
   */
  pack_locked: z.boolean().optional(),
  /** The newest order line of this item on an order that wasn't cancelled; null: never ordered. */
  last_ordered: z
    .object({
      po_id: z.uuid(),
      po_number: z.string(),
      ordered_at: instant,
      qty_packs: qtyString,
    })
    .nullable(),
  /** Fields a person wrote last (an import won't change them). */
  person_owned_fields: z.array(z.string()),
  source: sourceRefSchema.nullable(),
  archived_at: instant.nullable(),
});
/** A supplier item. */
export type SupplierItem = z.infer<typeof supplierItemSchema>;

/** A supplier in lists. */
export const supplierSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  /** false: its invoices carry no VAT. */
  vat_registered: z.boolean(),
  lead_time_days: leadDays.nullable(),
  /** Live supplier items. */
  item_count: count,
  source: sourceRefSchema.nullable(),
  archived_at: instant.nullable(),
});
/** A supplier in lists. */
export type SupplierSummary = z.infer<typeof supplierSummarySchema>;

/** `GET /purchasing/suppliers/:id`. */
export const supplierDetailSchema = supplierSummarySchema.extend({
  contact_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  note: z.string().nullable(),
  person_owned_fields: z.array(z.string()),
  imported_at: instant.nullable(),
  /** Live items first, then archived ones. */
  items: z.array(supplierItemSchema),
  created_at: instant,
  updated_at: instant,
});
/** A supplier with its items. */
export type SupplierDetail = z.infer<typeof supplierDetailSchema>;

/** `GET /purchasing/suppliers` query. */
export const supplierListQuerySchema = z.object({
  /** Name or code contains (case-insensitive). */
  q: z.string().trim().max(100).optional(),
  archived: queryBool,
  limit,
  cursor,
});
/** `GET /purchasing/suppliers` response (by name). */
export const supplierListResponseSchema = z.object({
  items: z.array(supplierSummarySchema),
  next_cursor: z.string().nullable(),
  total: count,
});
/** Response of `GET /purchasing/suppliers`. */
export type SupplierListResponse = z.infer<typeof supplierListResponseSchema>;

/** The supplier fields a person edits (every one optional; `null` clears it). */
const supplierFields = z.object({
  code: text(40).nullable().optional(),
  contact_name: text(200).nullable().optional(),
  email: text(200).nullable().optional(),
  phone: text(60).nullable().optional(),
  website: text(300).nullable().optional(),
  address: text(500).nullable().optional(),
  note: text(2000).nullable().optional(),
  /** false: the supplier is not VAT-registered (its invoices carry no VAT). */
  vat_registered: z.boolean().optional(),
  lead_time_days: leadDays.nullable().optional(),
});

/** `POST /purchasing/suppliers` body. */
export const supplierCreateRequestSchema = supplierFields.extend({ name: text(200) });
/** Body of `POST /purchasing/suppliers`. */
export type SupplierCreateRequest = z.infer<typeof supplierCreateRequestSchema>;

/**
 * `PUT /purchasing/suppliers/:id` body: only the fields sent change, and each becomes the
 * person's (an import then asks before changing it, D-032). `archived` archives / restores.
 */
export const supplierUpdateRequestSchema = supplierFields
  .extend({ name: text(200).optional(), archived: z.boolean().optional() })
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });
/** Body of `PUT /purchasing/suppliers/:id`. */
export type SupplierUpdateRequest = z.infer<typeof supplierUpdateRequestSchema>;

/** `POST /purchasing/suppliers/:id/items` body. */
export const supplierItemCreateRequestSchema = z.object({
  product_id: z.uuid(),
  supplier_code: text(64).optional(),
  description: text(300).optional(),
  pack_uom: uomCode,
  base_qty_per_pack: positiveQtyString,
  min_order_packs: positiveQtyString.optional(),
  lead_time_days: leadDays.optional(),
  preferred: z.boolean().optional(),
  vat_rate_code: vatCode.optional(),
});
/** Body of `POST /purchasing/suppliers/:id/items`. */
export type SupplierItemCreateRequest = z.infer<typeof supplierItemCreateRequestSchema>;

/** `PUT /purchasing/supplier-items/:id` body (product and supplier never change). */
export const supplierItemUpdateRequestSchema = z
  .object({
    supplier_code: text(64).nullable().optional(),
    description: text(300).nullable().optional(),
    pack_uom: uomCode.optional(),
    base_qty_per_pack: positiveQtyString.optional(),
    min_order_packs: positiveQtyString.nullable().optional(),
    lead_time_days: leadDays.nullable().optional(),
    preferred: z.boolean().optional(),
    vat_rate_code: vatCode.nullable().optional(),
    archived: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });
/** Body of `PUT /purchasing/supplier-items/:id`. */
export type SupplierItemUpdateRequest = z.infer<typeof supplierItemUpdateRequestSchema>;

/**
 * `POST /purchasing/supplier-items/:id/replace` body: the new pack (D-063). The new item
 * keeps the old one's product, codes, lead time, VAT code and preferred flag unless given;
 * the old one is archived (its prices and orders stay as they were). Answers the new item.
 */
export const supplierItemReplaceRequestSchema = z.object({
  pack_uom: uomCode,
  base_qty_per_pack: positiveQtyString,
  min_order_packs: positiveQtyString.nullable().optional(),
  supplier_code: text(64).nullable().optional(),
  description: text(300).nullable().optional(),
});
/** Body of `POST /purchasing/supplier-items/:id/replace`. */
export type SupplierItemReplaceRequest = z.infer<typeof supplierItemReplaceRequestSchema>;

// ---------------------------------------------------------------------------------------
// Price lists

/**
 * One price in a new version: the list price and either the discount or the net price
 * (the discount is then implied, half-up to a basis point). Neither: no discount.
 */
export const priceInputSchema = z
  .object({
    supplier_item_id: z.uuid(),
    list_price_cents: cents,
    discount_bp: bp.optional(),
    net_price_cents: cents.optional(),
  })
  .refine((p) => p.discount_bp === undefined || p.net_price_cents === undefined, {
    message: 'Give the discount or the net price, not both',
    path: ['net_price_cents'],
  })
  .refine((p) => p.net_price_cents === undefined || p.net_price_cents <= p.list_price_cents, {
    message: 'The net price is above the list price',
    path: ['net_price_cents'],
  });

/** `POST /purchasing/suppliers/:id/price-lists` body. */
export const priceListCreateRequestSchema = z
  .object({
    effective_from: plainDate,
    note: text(500).optional(),
    prices: z.array(priceInputSchema).min(1).max(5_000),
  })
  .superRefine((b, ctx) => {
    const seen = new Set<string>();
    b.prices.forEach((p, i) => {
      if (seen.has(p.supplier_item_id)) {
        ctx.addIssue({
          code: 'custom',
          message: 'This item is priced twice',
          path: ['prices', i, 'supplier_item_id'],
        });
      }
      seen.add(p.supplier_item_id);
    });
  });
/** Body of `POST /purchasing/suppliers/:id/price-lists`. */
export type PriceListCreateRequest = z.infer<typeof priceListCreateRequestSchema>;

/** A price list version in lists. */
export const priceListSummarySchema = z.object({
  id: z.uuid(),
  supplier: supplierRefSchema,
  version_no: z.int().min(1),
  effective_from: plainDate,
  /** `person`: entered in the Admin; `import`: the source's purchase costs. */
  origin: z.enum(['person', 'import']),
  note: z.string().nullable(),
  item_count: count,
  /** Items whose discount moved against their previous price (flagged). */
  discount_changes: count,
  created_by: personRefSchema.nullable(),
  created_at: instant,
  archived_at: instant.nullable(),
});
/** A price list version in lists. */
export type PriceListSummary = z.infer<typeof priceListSummarySchema>;

const priceValueSchema = z.object({
  list_price_cents: cents,
  discount_bp: bp,
  net_price_cents: cents,
});

/** One price of a version, against the item's price before it. */
export const priceListLineSchema = z.object({
  supplier_item: z.object({
    id: z.uuid(),
    product: stockProductRefSchema,
    supplier_code: z.string().nullable(),
    pack_uom: uomCode,
  }),
  list_price_cents: cents,
  discount_bp: bp,
  net_price_cents: cents,
  /** `new`: no earlier price; `changed` / `unchanged` against the previous one. */
  change: z.enum(['new', 'changed', 'unchanged']),
  previous: priceValueSchema.nullable(),
  /** The implied discount moved: shown flagged. */
  discount_changed: z.boolean(),
  /** Net change in % of the previous net (2 dp); null without a previous. */
  net_change_pct: z.string().nullable(),
});

/** `GET /purchasing/price-lists/:id` (and the answer to creating one). */
export const priceListDetailSchema = priceListSummarySchema.extend({
  prices: z.array(priceListLineSchema),
  /** One `discount_changed` flag per item whose discount moved. */
  flags: z.array(flagSchema),
});
/** A price list version with its prices. */
export type PriceListDetail = z.infer<typeof priceListDetailSchema>;

/** `GET /purchasing/suppliers/:id/price-lists` response. */
export const priceListListResponseSchema = z.object({ items: z.array(priceListSummarySchema) });

// ---------------------------------------------------------------------------------------
// Purchase requests

/** One line of `POST /purchasing/requests`. */
export const purchaseRequestLineInputSchema = z.object({
  supplier_item_id: z.uuid(),
  /** In the supplier item's packs. */
  qty_packs: positiveQtyString,
  note: text(500).optional(),
  /**
   * The restock suggestion it came from (inputs and steps), kept for the approver: in the
   * shape the restock endpoint answers, at most {@link KEPT_SUGGESTION_MAX_CHARS} as JSON.
   */
  suggestion: z.lazy(() => keptRestockSuggestionSchema).optional(),
});

/** `POST /purchasing/requests` body. One product at most once. */
export const purchaseRequestCreateRequestSchema = z.object({
  branch_id: z.uuid(),
  supplier_id: z.uuid(),
  needed_by: plainDate.optional(),
  note: text(1000).optional(),
  /** `suggestion` when the quantities came from the restock suggestion. */
  origin: z.enum(['admin', 'suggestion']).default('admin'),
  lines: z.array(purchaseRequestLineInputSchema).min(1).max(500),
});
/** Body of `POST /purchasing/requests`. */
export type PurchaseRequestCreateRequest = z.infer<typeof purchaseRequestCreateRequestSchema>;

/** A supplier item as order and request lines show it. */
export const supplierItemRefSchema = z.object({
  id: z.uuid(),
  supplier_code: z.string().nullable(),
  description: z.string().nullable(),
  pack_uom: uomCode,
  base_qty_per_pack: qtyString,
});

/** One line of a purchase request. */
export const purchaseRequestLineSchema = z.object({
  id: z.uuid(),
  line_no: z.int().min(1),
  product: stockProductRefSchema,
  supplier_item: supplierItemRefSchema,
  qty_packs: qtyString,
  base_qty: qtyString,
  /** Net price per pack when requested (ex VAT); null: no price known. */
  unit_net_cents: cents.nullable(),
  est_value_cents: cents.nullable(),
  note: z.string().nullable(),
  suggestion: z.record(z.string(), z.unknown()).nullable(),
  /** The manager's judgement of this product; null until decided. */
  decision: z.enum(['approved', 'rejected']).nullable(),
  approved_qty_packs: qtyString.nullable(),
});

/** A purchase request. */
export const purchaseRequestSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.enum(PURCHASE_REQUEST_STATUSES),
  origin: z.enum(PURCHASE_REQUEST_ORIGINS),
  branch: branchRefSchema,
  supplier: supplierRefSchema,
  needed_by: plainDate.nullable(),
  note: z.string().nullable(),
  requested_by: personRefSchema.nullable(),
  requested_at: instant,
  /** The approvals-inbox request that decides it. */
  approval_request_id: z.uuid().nullable(),
  /**
   * The order approving made: its status and its value ex VAT (what was really ordered, at
   * the order's prices; lines without a price are left out of it and counted).
   */
  purchase_order: z
    .object({
      id: z.uuid(),
      number: z.string(),
      status: z.enum(PO_STATUSES),
      subtotal_ex_vat_cents: cents,
      unpriced_lines: count,
    })
    .nullable(),
  decided_at: instant.nullable(),
  /** Σ of the priced lines; null when no line has a price. */
  est_value_cents: cents.nullable(),
  lines: z.array(purchaseRequestLineSchema),
});
/** A purchase request. */
export type PurchaseRequest = z.infer<typeof purchaseRequestSchema>;

/** `GET /purchasing/requests` query (newest first). */
export const purchaseRequestListQuerySchema = z.object({
  status: z.enum(PURCHASE_REQUEST_STATUSES).optional(),
  branch: z.uuid().optional(),
  supplier: z.uuid().optional(),
  limit,
  cursor,
});
/** `GET /purchasing/requests` response. */
export const purchaseRequestListResponseSchema = z.object({
  items: z.array(purchaseRequestSchema),
  next_cursor: z.string().nullable(),
});

/**
 * Per-product judgement of a purchase request in `POST /approvals/:id/decision`
 * (`purchase_lines`, approve only): each product approved (optionally with fewer or more
 * packs) or rejected. Products not listed are approved as requested. At least one product
 * must stay approved (else reject the request).
 */
export const purchaseLineDecisionSchema = z.object({
  product_id: z.uuid(),
  decision: z.enum(['approve', 'reject']),
  qty_packs: positiveQtyString.optional(),
});
/** One product's judgement. */
export type PurchaseLineDecision = z.infer<typeof purchaseLineDecisionSchema>;

// ---------------------------------------------------------------------------------------
// Purchase orders

/** One line of an order being made or edited. */
export const poLineInputSchema = z.object({
  supplier_item_id: z.uuid(),
  qty_packs: positiveQtyString,
  /** Net price per pack, ex VAT; default: the price list in effect today (or none). */
  unit_net_cents: cents.optional(),
  note: text(500).optional(),
});

/** `POST /purchasing/orders` body (an order without a request). */
export const poCreateRequestSchema = z.object({
  branch_id: z.uuid(),
  supplier_id: z.uuid(),
  expected_date: plainDate.optional(),
  note: text(1000).optional(),
  lines: z.array(poLineInputSchema).min(1).max(500),
});
/** Body of `POST /purchasing/orders`. */
export type PoCreateRequest = z.infer<typeof poCreateRequestSchema>;

/**
 * `PUT /purchasing/orders/:id` body. `lines` replaces every line (only before anything was
 * received: 409 `po_has_receipts`); details can change while the order is open.
 */
export const poUpdateRequestSchema = z
  .object({
    expected_date: plainDate.nullable().optional(),
    note: text(1000).nullable().optional(),
    lines: z.array(poLineInputSchema).min(1).max(500).optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });
/** Body of `PUT /purchasing/orders/:id`. */
export type PoUpdateRequest = z.infer<typeof poUpdateRequestSchema>;

/** `POST /purchasing/orders/:id/send` body: how it went out (a note; nothing is sent). */
export const poSendRequestSchema = z.object({ note: text(500).optional() });
/** `POST /purchasing/orders/:id/cancel` and `/close` body. */
export const poReasonRequestSchema = z.object({ reason: text(500) });

/** How much of a line has arrived. */
export const LINE_RECEIVING = ['none', 'partial', 'full', 'over'] as const;

/** One line of an order. */
export const poLineSchema = z.object({
  id: z.uuid(),
  line_no: z.int().min(1),
  product: stockProductRefSchema,
  supplier_item: supplierItemRefSchema,
  qty_packs: qtyString,
  pack_uom: uomCode,
  base_qty_per_pack: qtyString,
  ordered_base_qty: qtyString,
  received_base_qty: qtyString,
  outstanding_base_qty: qtyString,
  receiving: z.enum(LINE_RECEIVING),
  list_price_cents: cents.nullable(),
  discount_bp: bp.nullable(),
  unit_net_cents: cents.nullable(),
  /** The price list version the price came from (null: typed, or none). */
  price_list_id: z.uuid().nullable(),
  vat_rate_code: vatCode,
  line_ex_vat_cents: cents.nullable(),
  line_vat_cents: cents.nullable(),
  line_total_cents: cents.nullable(),
  /** Stock cost per base unit (ex VAT when the account claims VAT). */
  unit_cost_cents: cents.nullable(),
  note: z.string().nullable(),
});

/** A purchase order in lists. */
export const poSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.enum(PO_STATUSES),
  branch: branchRefSchema,
  supplier: supplierRefSchema,
  expected_date: plainDate.nullable(),
  purchase_request: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  subtotal_ex_vat_cents: cents,
  vat_cents: cents,
  total_cents: cents,
  unpriced_lines: count,
  line_count: count,
  /** Lines that something has arrived for. */
  lines_received: count,
  /** Lines everything ordered has arrived for. */
  lines_complete: count,
  /** When the newest delivery against it arrived; null: none yet. */
  last_received_at: instant.nullable(),
  receiving: z.enum(['none', 'partial', 'full']),
  created_at: instant,
  sent_at: instant.nullable(),
});
/** A purchase order in lists. */
export type PoSummary = z.infer<typeof poSummarySchema>;

/** A goods receipt in lists (and on its order). */
export const receiptSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  branch: branchRefSchema,
  location: stockLocationRefSchema,
  po: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  supplier: supplierRefSchema.nullable(),
  received_at: instant,
  trading_day: plainDate,
  supplier_doc_ref: z.string().nullable(),
  received_by: personRefSchema.nullable(),
  line_count: count,
  /** Flags on the receipt and its lines (over / under / unexpected / best-before …). */
  flag_count: count,
});
/** A goods receipt in lists. */
export type ReceiptSummary = z.infer<typeof receiptSummarySchema>;

/** `GET /purchasing/orders/:id`. */
export const poDetailSchema = poSummarySchema.extend({
  supplier_vat_registered: z.boolean(),
  account_vat_mode: z.enum(['registered', 'non_vat']),
  /** Input VAT the account can claim back (0 for a non-VAT account). */
  claimable_vat_cents: cents,
  note: z.string().nullable(),
  created_by: personRefSchema.nullable(),
  sent_by: personRefSchema.nullable(),
  sent_note: z.string().nullable(),
  closed_at: instant.nullable(),
  closed_by: personRefSchema.nullable(),
  close_reason: z.string().nullable(),
  cancelled_at: instant.nullable(),
  cancelled_by: personRefSchema.nullable(),
  cancel_reason: z.string().nullable(),
  lines: z.array(poLineSchema),
  receipts: z.array(receiptSummarySchema),
  /** What a person may still do (@enp/domain poActions). */
  actions: z.object({
    edit_lines: z.boolean(),
    edit_details: z.boolean(),
    mark_sent: z.boolean(),
    cancel: z.boolean(),
    close: z.boolean(),
  }),
});
/** A purchase order with its lines and receipts. */
export type PoDetail = z.infer<typeof poDetailSchema>;

/** `GET /purchasing/orders` query (newest first). `open=true`: still expecting deliveries. */
export const poListQuerySchema = z.object({
  status: z.enum(PO_STATUSES).optional(),
  open: queryBool,
  supplier: z.uuid().optional(),
  branch: z.uuid().optional(),
  expected_from: plainDate.optional(),
  expected_to: plainDate.optional(),
  limit,
  cursor,
});
/** `GET /purchasing/orders` response. */
export const poListResponseSchema = z.object({
  items: z.array(poSummarySchema),
  next_cursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------------------
// Goods receipts

/** One received line. */
export const receiptLineOutSchema = z.object({
  id: z.uuid(),
  line_no: z.int().min(1),
  product: stockProductRefSchema,
  po_line_id: z.uuid().nullable(),
  supplier_item_id: z.uuid().nullable(),
  qty: qtyString,
  uom: uomCode,
  /** In the product's base unit; null when the unit could not be converted (not booked). */
  base_qty: qtyString.nullable(),
  supplier_lot_ref: z.string().nullable(),
  best_before: plainDate.nullable(),
  lot: lotRefSchema,
  location: stockLocationRefSchema,
  state: z.string(),
  movement_id: z.uuid().nullable(),
  unit_cost_cents: cents.nullable(),
  /** `over_received`, `under_received`, `unexpected_item`, `missing_best_before`, … */
  flags: z.array(flagSchema),
});

/** `GET /purchasing/receipts/:id`. */
export const receiptDetailSchema = receiptSummarySchema.extend({
  note: z.string().nullable(),
  device_id: z.uuid().nullable(),
  command_id: z.uuid().nullable(),
  /** Receipt-level flags (`not_delivered`, `po_not_found`, `po_cancelled`, …). */
  flags: z.array(flagSchema),
  lines: z.array(receiptLineOutSchema),
});
/** A goods receipt with its lines. */
export type ReceiptDetail = z.infer<typeof receiptDetailSchema>;

/** `GET /purchasing/receipts` query (newest first; `from`/`to` are trading days). */
export const receiptListQuerySchema = z.object({
  po: z.uuid().optional(),
  supplier: z.uuid().optional(),
  branch: z.uuid().optional(),
  from: plainDate.optional(),
  to: plainDate.optional(),
  /** Only receipts with at least one flag. */
  flagged: queryBool,
  limit,
  cursor,
});
/** `GET /purchasing/receipts` response. */
export const receiptListResponseSchema = z.object({
  items: z.array(receiptSummarySchema),
  next_cursor: z.string().nullable(),
});

/**
 * A delivery a station recorded that the server couldn't book as sent, held for a manager
 * in the Admin's held actions (D-066, review UI6A-102). The goods are there but not yet in
 * stock, and the order doesn't count them as arrived until it is booked.
 */
export const heldDeliverySchema = z.object({
  /** The held action that resolves it (`/settings/held-actions/:id`). */
  hold_id: z.uuid(),
  /** Why it was held (`physical_rejected`, …) and the server's words. */
  reason: z.string(),
  message: z.string().nullable(),
  /** The receipt number the station gave it (`GR-…`); null when it sent none. */
  number: z.string().nullable(),
  /** When the station says it was received; null when unreadable. */
  received_at: instant.nullable(),
  /** When the server held it. */
  held_at: instant,
  branch: branchRefSchema,
  /** The order it was received against (null: none, or not one of this account's). */
  po: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  supplier: supplierRefSchema.nullable(),
  supplier_doc_ref: z.string().nullable(),
  received_by: personRefSchema.nullable(),
  /** The lines as sent: the product (null when the id is unknown), as counted. */
  lines: z.array(
    z.object({
      product: stockProductRefSchema.nullable(),
      qty: z.string(),
      uom: z.string(),
    }),
  ),
});
/** A held delivery. */
export type HeldDelivery = z.infer<typeof heldDeliverySchema>;

/** `GET /purchasing/receipts/held` query: open holds only, newest first. */
export const heldDeliveryListQuerySchema = z.object({ po: z.uuid().optional() });
/** `GET /purchasing/receipts/held` response. */
export const heldDeliveryListResponseSchema = z.object({ items: z.array(heldDeliverySchema) });
/** Response of `GET /purchasing/receipts/held`. */
export type HeldDeliveryListResponse = z.infer<typeof heldDeliveryListResponseSchema>;

// ---------------------------------------------------------------------------------------
// Restock levels (minimum and par) and the restock suggestion

/**
 * A product's minimum stock and par (order-up-to) level, in its base unit, for one branch
 * or all (`branch_id` null). The suggestion uses the branch's own row, else the all-branch
 * one. Both null: cleared.
 */
export const restockLevelSchema = z.object({
  product: stockProductRefSchema,
  branch_id: z.uuid().nullable(),
  min_qty: qtyString.nullable(),
  par_qty: qtyString.nullable(),
  updated_at: instant,
});
/** A restock level. */
export type RestockLevel = z.infer<typeof restockLevelSchema>;

/** `GET /purchasing/restock-levels` response (by product name). */
export const restockLevelListResponseSchema = z.object({ items: z.array(restockLevelSchema) });

/** `PUT /purchasing/restock-levels` body: sets (or clears, with nulls) one product's levels. */
export const restockLevelUpdateRequestSchema = z
  .object({
    product_id: z.uuid(),
    branch_id: z.uuid().nullable(),
    min_qty: nonNegativeQtyString.nullable(),
    par_qty: positiveQtyString.nullable(),
  })
  .refine(
    (b) => b.min_qty === null || b.par_qty === null || compareQty(b.min_qty, b.par_qty) <= 0,
    { message: 'The minimum is above the par level', path: ['min_qty'] },
  );
/** Body of `PUT /purchasing/restock-levels`. */
export type RestockLevelUpdateRequest = z.infer<typeof restockLevelUpdateRequestSchema>;

/**
 * `GET /purchasing/restock` query. The history is a branch's real stock and sales
 * (`branch_id`, default: the account's default branch) or a sales replay's
 * (`replay_scenario_id`: its hidden branch; owners and all-branch managers only). `as_of`
 * (default: today's trading day) is the last day of the window and the day of the stock
 * position; `days` the window length.
 */
export const restockQuerySchema = z
  .object({
    branch_id: z.uuid().optional(),
    replay_scenario_id: z.uuid().optional(),
    as_of: plainDate.optional(),
    days: z.coerce.number().int().min(7).max(365).default(84),
    supplier_id: z.uuid().optional(),
    review_days: z.coerce.number().int().min(0).max(90).default(7),
    safety_days: z.coerce.number().int().min(0).max(90).default(0),
    /** Include products with nothing to order (default: only those to order). */
    include_zero: queryBool,
  })
  .refine((q) => q.branch_id === undefined || q.replay_scenario_id === undefined, {
    message: 'Give a branch or a replay scenario, not both',
    path: ['replay_scenario_id'],
  });
/** Parsed `GET /purchasing/restock` query. */
export type RestockQuery = z.infer<typeof restockQuerySchema>;

/** One step of a suggestion's reasoning. */
export const restockStepSchema = z.object({
  code: z.string(),
  value: z.string(),
  text: z.string(),
});

/** A purchase request still waiting for a decision, as a restock line names it. */
export const waitingRequestRefSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  requested_at: instant,
  /** What its line for the product asks for: in its packs, and in the base unit. */
  qty_packs: qtyString,
  pack_uom: uomCode,
  base_qty: qtyString,
});
/** A waiting request a restock line names. */
export type WaitingRequestRef = z.infer<typeof waitingRequestRefSchema>;

/** An open purchase order still expecting a product, as a restock line names it. */
export const openOrderRefSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  expected_date: plainDate.nullable(),
  /** Still to arrive of the product, in its base unit. */
  outstanding_base_qty: qtyString,
});
/** An open order a restock line names. */
export type OpenOrderRef = z.infer<typeof openOrderRefSchema>;

/** One product's suggestion, with everything it was worked out from. */
export const restockItemSchema = z.object({
  product: stockProductRefSchema,
  supplier: supplierRefSchema.nullable(),
  supplier_item: supplierItemRefSchema.nullable(),
  suggested_base_qty: qtyString,
  suggested_packs: qtyString.nullable(),
  need_qty: qtyString,
  target_qty: qtyString,
  ceiling_qty: qtyString.nullable(),
  /** Net price per pack in effect on `as_of`; null: none. */
  unit_net_cents: cents.nullable(),
  /** suggested packs × price; null without a price or packs. */
  est_value_cents: cents.nullable(),
  explanation: z.object({
    inputs: z.object({
      window_days: count,
      in_stock_days: count,
      out_of_stock_days: count,
      used_in_stock_days: qtyString,
      daily_rate: qtyString,
      on_hand_qty: qtyString,
      on_order_qty: qtyString,
      /**
       * Asked for on this branch's purchase requests still waiting for a decision: counted
       * like stock on order (UI6A-101). Absent on suggestions kept before it existed.
       */
      requested_qty: qtyString.optional(),
      lead_time_days: count,
      lead_time_defaulted: z.boolean(),
      review_days: count,
      safety_days: count,
      shelf_life_days: count.nullable(),
      pack_base_qty: qtyString.nullable(),
      min_order_packs: qtyString.nullable(),
      min_qty: qtyString.nullable(),
      par_qty: qtyString.nullable(),
      /** Where the usage came from: sold (every state) and consumed by production. */
      sold_qty: qtyString,
      consumed_qty: qtyString,
    }),
    steps: z.array(restockStepSchema),
  }),
  flags: z.array(flagSchema),
  /**
   * The branch's purchase requests still waiting for a decision that ask for this product
   * (their sum is `requested_qty`), oldest first. Absent on suggestions kept before it existed.
   */
  waiting_requests: z.array(waitingRequestRefSchema).optional(),
  /**
   * The branch's open purchase orders still expecting this product (their sum is
   * `on_order_qty`), by expected date. Absent on suggestions kept before it existed.
   */
  open_orders: z.array(openOrderRefSchema).optional(),
});
/** A restock suggestion line. */
export type RestockItem = z.infer<typeof restockItemSchema>;

/** The largest restock suggestion a request line keeps, as JSON characters. */
export const KEPT_SUGGESTION_MAX_CHARS = 20_000;

/**
 * A restock suggestion as a purchase request line keeps it for the approver (review
 * CR6-16): where the history came from, its words, the day, and the suggestion itself.
 * Checked on the way in (unknown keys dropped, size bounded).
 */
export const keptRestockSuggestionSchema = z
  .object({
    source: z.enum(['real', 'replay']),
    history: z.string().max(300),
    as_of: plainDate,
    item: restockItemSchema,
  })
  .refine((s) => JSON.stringify(s).length <= KEPT_SUGGESTION_MAX_CHARS, {
    message: `The suggestion kept with a line is at most ${KEPT_SUGGESTION_MAX_CHARS} characters`,
  });
/** A restock suggestion kept with a request line. */
export type KeptRestockSuggestion = z.infer<typeof keptRestockSuggestionSchema>;

/** `GET /purchasing/restock` response (products to order first, by value, then name). */
export const restockResponseSchema = z.object({
  source: z.enum(['real', 'replay']),
  branch: branchRefSchema,
  as_of: plainDate,
  window: z.object({ from: plainDate, to: plainDate, days: count }),
  items: z.array(restockItemSchema),
  coverage: z.object({
    /** Products with a live supplier item (of `supplier_id`, when given). */
    products_considered: count,
    /** Of those, products used at least once in the window. */
    with_usage: count,
    /** Of those, products with something to order. */
    to_order: count,
    summary: z.string(),
  }),
});
/** Response of `GET /purchasing/restock`. */
export type RestockResponse = z.infer<typeof restockResponseSchema>;
